// AGC: EmployeeSummary - Shows employees who used a machine
// AGC: Calculates summary from work logs
// AGC: Groups by operatorUserId and shows total hours

import React, { useMemo } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import type { MachineWorkLog } from '../../types/machineWorkLog'
import { User, Clock, MapPin, Loader2 } from 'lucide-react'

interface EmployeeSummaryProps {
  workLogs: MachineWorkLog[]
  isLoading?: boolean
  maxItems?: number
}

/**
 * AGC: Employee summary data calculated from work logs
 */
interface EmployeeSummaryData {
  operatorUserId: string
  operatorName: string
  operatorAvatar?: string
  totalHours: number
  totalUses: number
  lastUsed: string
  lotsWorked: Set<string>
}

/**
 * AGC: EmployeeSummary component
 */
export const EmployeeSummary: React.FC<EmployeeSummaryProps> = ({
  workLogs,
  isLoading = false,
  maxItems = 5,
}) => {
  const { t } = useTranslation()

  // AGC: Calculate employee summaries from work logs
  const employeeSummaries = useMemo(() => {
    const summaryMap = new Map<string, EmployeeSummaryData>()

    workLogs.forEach((log) => {
      const existing = summaryMap.get(log.operatorUserId)
      
      if (existing) {
        existing.totalHours += log.hoursUsed
        existing.totalUses += 1
        if (log.lotId) {
          existing.lotsWorked.add(log.lotId)
        }
        // AGC: Update lastUsed if this log is more recent
        if (log.date > existing.lastUsed) {
          existing.lastUsed = log.date
        }
      } else {
        summaryMap.set(log.operatorUserId, {
          operatorUserId: log.operatorUserId,
          operatorName: log.operatorName,
          operatorAvatar: log.operatorAvatar,
          totalHours: log.hoursUsed,
          totalUses: 1,
          lastUsed: log.date,
          lotsWorked: new Set(log.lotId ? [log.lotId] : []),
        })
      }
    })

    // AGC: Convert to array and sort by total hours (descending)
    return Array.from(summaryMap.values())
      .sort((a, b) => b.totalHours - a.totalHours)
      .slice(0, maxItems)
  }, [workLogs, maxItems])

  // AGC: Format hours for display
  const formatHours = (hours: number): string => {
    return `${hours.toFixed(1)}h`
  }

  // AGC: Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    )
  }

  // AGC: Empty state
  if (employeeSummaries.length === 0) {
    return (
      <div className="text-center py-4">
        <User className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('machines.detail.noUsageLogs')}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {employeeSummaries.map((employee) => (
        <div
          key={employee.operatorUserId}
          className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
        >
          {/* AGC: Avatar */}
          <div className="flex-shrink-0">
            {employee.operatorAvatar ? (
              <img
                src={employee.operatorAvatar}
                alt={employee.operatorName}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <User className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            )}
          </div>

          {/* AGC: Info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {employee.operatorName}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span>{employee.totalUses} {employee.totalUses === 1 ? 'uso' : 'usos'}</span>
              <span className="text-gray-300 dark:text-gray-600">•</span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {employee.lotsWorked.size} {employee.lotsWorked.size === 1 ? 'lote' : 'lotes'}
              </span>
            </div>
          </div>

          {/* AGC: Hours */}
          <div className="flex-shrink-0 text-right">
            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
              {formatHours(employee.totalHours)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('machines.detail.totalHours').toLowerCase()}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}

export default EmployeeSummary
